import { Identify, identify } from '@amplitude/analytics-browser'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import legalEntities from 'modules/core/store/collections/legalEntities'

import type { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'

const userDataPropertyName = {
  sentMessage: 'sent message',
  taskCompleted: 'task completed',
  bookkeeperFirm: 'bookkeeper firm',
}

export const PROFESSIONALS_APP = 'professionals'
export const FACTORIAL_APP = 'factorial'
const INHOUSE_MANAGEMENT = 'internal'
const PRO_MANAGEMENT = 'professionals'

export const toIncidencesSettings = {
  fromBanner: 'incidences_banner',
  fromButton: 'incidences_button',
  fromPayrollSettings: 'payroll_settings',
}

export const clientsSection = (isPreferencesActive: boolean) => {
  if (isPreferencesActive) return 'preferences'

  return 'exportable-files'
}

const getFilterType = (key: string) => {
  switch (key) {
    case 'name':
      return 'incidence_type'
    case 'legal_entity_id':
      return 'legal_entity'
    case 'status':
      return 'status'
    case 'date':
      return 'date'
    case 'custom_leave_name':
      return 'custom_leave_name'
    default:
      throw new Error('Unknown incidence type')
  }
}

const setFilterValue = (key: string) => {
  switch (key) {
    case 'in-preparation':
      return 'in preparation'
    case 'to-do':
      return 'to do'
    case '':
      return 'all'
    case 'to-do,doing,reopened':
      return 'all active'
    case 'manual_incidence':
      return 'manual incidence'
    case 'custom-leave':
      return 'custom leave'
    case 'sick,parental,custom-leave':
      return 'all absences'
    default:
      return key
  }
}

const setUserData = (name: string, value: boolean) => {
  const identity = new Identify()
  identity.setOnce(name, value)
  identify(identity)
}

export const setBookkeeperCompany = (email: string) => {
  const bookkeeperCompany = email.split('@')?.[1]?.split('.')?.[0] || ''
  setUserData(userDataPropertyName.bookkeeperFirm, !!bookkeeperCompany)
}

const getLegalEntityName = (id: string) => {
  const legal_entity = legalEntities.find({ id })
  return legal_entity?.attributes.legal_name
}

export const trackIncidenceFilter = (
  filterType: string,
  result: string,
  bookkeeperView: boolean,
  inhouse: boolean
) => {
  let value
  if (filterType === 'legal_entity_id' && result !== '') {
    value = getLegalEntityName(result)
  } else {
    value = setFilterValue(result)
  }

  const incidence_type = getFilterType(filterType)
  track(trackingEvents.bookkeeper.incidences.applyFilter, {
    filter_type: filterType,
    [incidence_type]: value,
    application: bookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
    management: inhouse ? INHOUSE_MANAGEMENT : PRO_MANAGEMENT,
  })
}

const handleLegalEntity = (incidence: IncidenceQueryData | undefined) => {
  if (!incidence) return

  return incidence.legalEntity?.legalName
}

export const trackViewIncidenceDetail = (
  incidence: IncidenceQueryData | undefined,
  bookkeeperView: boolean,
  inhouse: boolean
) => {
  if (!incidence) return

  const legal_entity = handleLegalEntity(incidence)

  track(trackingEvents.bookkeeper.incidences.viewIncidenceDetail, {
    legal_entity: legal_entity,
    incidence_id: incidence.id,
    incidence_type: incidence.name,
    incidence_status: setFilterValue(incidence.status),
    application: bookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
    management: inhouse ? INHOUSE_MANAGEMENT : PRO_MANAGEMENT,
  })
}

export const trackIncidenceChangeStatus = (
  incidence: IncidenceQueryData,
  status: string,
  bookkeeperView: boolean,
  inhouse: boolean
) => {
  if (!incidence) return

  const legal_entity = handleLegalEntity(incidence)

  track(trackingEvents.bookkeeper.incidences.changeStatus, {
    legal_entity: legal_entity,
    incidence_id: incidence.id,
    incidence_status: setFilterValue(status),
    incidence_type: incidence.name,
    application: bookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
    management: inhouse ? INHOUSE_MANAGEMENT : PRO_MANAGEMENT,
  })
}

type bulkActionTypes = 'discard' | 'send' | 'done' | 'to-do' | 'doing'

export const trackIncidenceChangeStatusBulk = (
  action: bulkActionTypes,
  incidencesChanged: number,
  bookkeeperView: boolean
) => {
  track(trackingEvents.bookkeeper.incidences.changeStatusBulk, {
    action,
    incidencesChanged,
    application: bookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
  })
}

export const trackIncidenceSentMessage = (
  incidence: IncidenceQueryData | undefined | null,
  bookkeeperView: boolean,
  files: File[]
) => {
  if (!incidence) return

  setUserData(userDataPropertyName.sentMessage, true)
  const legal_entity = handleLegalEntity(incidence)

  track(trackingEvents.bookkeeper.incidences.sentMessage, {
    legal_entity: legal_entity,
    file_attached: !!files.length,
    incidence_id: incidence.id,
    incidence_type: setFilterValue(incidence.name),
    incidence_status: setFilterValue(incidence.status),
    application: bookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
  })
}

export const trackIncidenceCompleteTask = (
  incidence: IncidenceQueryData | undefined,
  taskName: string,
  bookkeeperView: boolean,
  inhouse: boolean
) => {
  if (!incidence) return

  setUserData(userDataPropertyName.taskCompleted, true)
  const legal_entity = handleLegalEntity(incidence)

  track(trackingEvents.bookkeeper.incidences.completeTask, {
    task_name: taskName,
    legal_entity: legal_entity,
    application: bookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
    incidence_id: incidence?.id,
    incidence_type: setFilterValue(incidence.name),
    incidence_status: setFilterValue(incidence.status),
    management: inhouse ? INHOUSE_MANAGEMENT : PRO_MANAGEMENT,
  })
}

export const trackAddIncidenceNote = (
  incidence: IncidenceQueryData | undefined,
  bookkeeperView: boolean,
  inhouse: boolean
) => {
  if (!incidence) return

  const legal_entity = handleLegalEntity(incidence)

  track(trackingEvents.bookkeeper.incidences.addNote, {
    legal_entity: legal_entity,
    application: bookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
    incidence_id: incidence?.id,
    incidence_type: setFilterValue(incidence.name),
    incidence_status: setFilterValue(incidence.status),
    management: inhouse ? INHOUSE_MANAGEMENT : PRO_MANAGEMENT,
  })
}
export const trackSwitchDetailTab = (
  incidence: IncidenceQueryData | undefined,
  tabName: string,
  bookkeeperView: boolean,
  inhouse: boolean
) => {
  if (!incidence) return

  const legal_entity = handleLegalEntity(incidence)

  track(trackingEvents.bookkeeper.incidences.switchIncidenceTabDetail, {
    tab_name: tabName,
    legal_entity: legal_entity,
    incidence_id: incidence?.id,
    incidence_type: setFilterValue(incidence.name),
    incidence_status: setFilterValue(incidence.status),
    application: bookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
    management: inhouse ? INHOUSE_MANAGEMENT : PRO_MANAGEMENT,
  })
}

export const trackLoadIncidenceTable = (
  numberOfIncidences: number,
  bookkeeperView: boolean,
  inhouse: boolean
) => {
  track(trackingEvents.bookkeeper.incidences.loadTable, {
    application: bookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
    number_incidences: numberOfIncidences,
    management: inhouse ? INHOUSE_MANAGEMENT : PRO_MANAGEMENT,
  })
}

export const trackPayrollResultsSubmitted = (
  period: string,
  period_id: string,
  payslip_document: boolean,
  payroll_summary: true
) => {
  track(trackingEvents.bookkeeper.incidences.submitPayrollResults, {
    application: PROFESSIONALS_APP,
    period: period,
    period_id: period_id,
    payslip_document: payslip_document,
    payroll_summary: payroll_summary,
  })
}
